/**
 * Admin V3 — Booking driver reassignment modal (AV3-M1)
 *
 * Operator reassigns a booking to a new available driver via
 * POST /admin/control-tower/bookings/{id}/reassign (RBAC: admin:bookings:assign).
 * Available drivers are loaded on open from
 * GET /admin/control-tower/drivers/available (RBAC: admin:drivers:view).
 * Parity with the legacy V2 BookingDetailPage reassign flow: driver selection +
 * typed reason + optional detail. No new business logic.
 */
import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

const BookingReassignModal = ({ open, bookingId, booking, reasons = [], mode = 'reassign', onClose, onDone, locale = DEFAULT_LOCALE }) => {
  const m = useAdminMutation(`/admin/control-tower/bookings/${bookingId || '_'}/reassign`, 'post');
  const { showSuccess, showError } = useToast();

  // Assign (no current driver) vs reassign (replace existing driver). Both route
  // through the canonical /reassign endpoint (it dispatches to the assign
  // primitive when the booking has no driver), only the wording differs.
  const prefix = mode === 'assign' ? 'bookings.assign' : 'bookings.reassign';
  const tk = (suffix) => t(`${prefix}.${suffix}`, locale);

  // Load available drivers only while the modal is open (parity: V2 fetched on open).
  const { data: driversData, loading: driversLoading } = useAdminQuery(
    '/admin/control-tower/drivers/available',
    {},
    { enabled: open },
  );
  const drivers = driversData?.drivers || [];

  const [driverId, setDriverId] = useState('');
  const [reasonType, setReasonType] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');

  if (!open || !bookingId) return null;

  const valid = Boolean(driverId) && Boolean(reasonType);

  const submit = () => {
    m.mutate(
      {
        new_driver_id: driverId,
        reason_type: reasonType,
        reason_detail: reasonDetail || null,
      },
      {
        onSuccess: () => {
          showSuccess(tk('ok'));
          if (onDone) onDone();
          onClose();
        },
        onError: (e) => showError(e?.response?.data?.detail || tk('error')),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-booking-reassign-modal">
      <div className="w-[520px] max-w-[94vw] rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[17px] font-semibold text-slate-900">{tk('title')}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-booking-reassign-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-4 text-[12px] text-slate-500">
          {tk('description').replace('{ref}', booking?.booking_reference || '')}
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-[12px] font-semibold text-slate-600">{tk('driverLabel')}</label>
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              className={inputCls}
              disabled={driversLoading}
              data-testid="adminv3-booking-reassign-driver"
            >
              <option value="">
                {driversLoading ? tk('driversLoading') : tk('driverPlaceholder')}
              </option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} — {d.license_plate}
                </option>
              ))}
            </select>
            {!driversLoading && drivers.length === 0 && (
              <p className="mt-1 text-[12px] text-slate-400" data-testid="adminv3-booking-reassign-no-drivers">
                {tk('noDrivers')}
              </p>
            )}
          </div>

          <div>
            <label className="text-[12px] font-semibold text-slate-600">{tk('reasonLabel')}</label>
            <select
              value={reasonType}
              onChange={(e) => setReasonType(e.target.value)}
              className={inputCls}
              data-testid="adminv3-booking-reassign-reason"
            >
              <option value="">{tk('reasonPlaceholder')}</option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[12px] font-semibold text-slate-600">{tk('detailLabel')}</label>
            <textarea
              value={reasonDetail}
              onChange={(e) => setReasonDetail(e.target.value)}
              rows={2}
              className={inputCls}
              data-testid="adminv3-booking-reassign-detail"
            />
          </div>
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-booking-reassign-missing">
            {tk('required')}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-booking-reassign-dismiss">
            {tk('cancel')}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={submit}
            disabled={!valid || m.loading}
            data-testid="adminv3-booking-reassign-confirm"
          >
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {tk('confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingReassignModal;
